import { useState, useEffect } from 'react';
import axios from 'axios';
import { FileText, Building, Users, Briefcase, CreditCard, CheckCircle, Plus, X, AlertCircle, Upload } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Toaster, toast } from 'sonner';
import { fileToCompressedDataUrl } from '../lib/imageCompress';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function errMsg(e, fallback) {
  const detail = e?.response?.data?.detail;
  if (Array.isArray(detail)) return detail.map(d => d.msg || JSON.stringify(d)).join('; ');
  if (typeof detail === 'string') return detail;
  if (e?.response?.status) return `${fallback} (${e.response.status})`;
  return e?.message || fallback;
}

function SuccessScreen() {
  return (
    <div className="min-h-screen bg-slate-50 pt-20" data-testid="ta-success-screen">
      <div className="max-w-2xl mx-auto px-4 py-20 text-center">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-10 h-10 text-green-600" />
        </div>
        <h1 className="text-3xl font-bold text-[#0f172a] mb-4">Application Submitted</h1>
        <p className="text-slate-600 mb-8">Thank you for your trade credit application. Our team will review your application and contact you within 2-3 business days.</p>
        <Button onClick={() => window.location.href = '/'} className="bg-[#01195d] hover:bg-[#01195d]/90">Return to Homepage</Button>
      </div>
    </div>
  );
}

function StepProgress({ currentStep }) {
  const steps = [
    { num: 1, title: 'Company' },
    { num: 2, title: 'Directors' },
    { num: 3, title: 'References' },
    { num: 4, title: 'Credit' },
    { num: 5, title: 'Declaration' }
  ];
  
  return (
    <div className="flex justify-between items-center mb-8">
      {steps.map((step, i) => (
        <div key={step.num} className="flex items-center">
          <div className="flex flex-col items-center">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold ${currentStep >= step.num ? 'bg-[#01195d] text-white' : 'bg-slate-200 text-slate-500'}`}>
              {currentStep > step.num ? <CheckCircle className="w-5 h-5" /> : step.num}
            </div>
            <span className={`text-xs mt-2 hidden md:block ${currentStep >= step.num ? 'text-[#01195d] font-medium' : 'text-slate-500'}`}>{step.title}</span>
          </div>
          {i < steps.length - 1 && <div className={`h-1 w-12 md:w-20 mx-2 rounded ${currentStep > step.num ? 'bg-[#01195d]' : 'bg-slate-200'}`} />}
        </div>
      ))}
    </div>
  );
}

function Step1({ form, updateField }) {
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <Building className="w-6 h-6 text-[#01195d]" />
        <h2 className="text-xl font-bold text-[#0f172a]">Company Details</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="md:col-span-2"><Label>Company Name *</Label><Input data-testid="ta-company-name" value={form.company_name} onChange={e => updateField('company_name', e.target.value)} className="mt-1.5" placeholder="Legal business name" /></div>
        <div><Label>Trading Name</Label><Input data-testid="ta-trading-name" value={form.trading_name} onChange={e => updateField('trading_name', e.target.value)} className="mt-1.5" placeholder="Trading as..." /></div>
        <div><Label>ABN *</Label><Input data-testid="ta-abn" value={form.abn} onChange={e => updateField('abn', e.target.value)} className="mt-1.5" placeholder="XX XXX XXX XXX" /></div>
        <div><Label>Years Trading</Label><Input data-testid="ta-years-trading" value={form.years_trading} onChange={e => updateField('years_trading', e.target.value)} className="mt-1.5" placeholder="e.g., 5" /></div>
        <div><Label>Business Phone</Label><Input data-testid="ta-business-phone" value={form.business_phone} onChange={e => updateField('business_phone', e.target.value)} className="mt-1.5" placeholder="(02) XXXX XXXX" /></div>
        <div className="md:col-span-2"><Label>Trading Address *</Label><Input data-testid="ta-trading-address" value={form.trading_address} onChange={e => updateField('trading_address', e.target.value)} className="mt-1.5" placeholder="Street address" /></div>
        <div><Label>Postcode</Label><Input data-testid="ta-trading-postcode" value={form.trading_postcode} onChange={e => updateField('trading_postcode', e.target.value)} className="mt-1.5" placeholder="XXXX" /></div>
        <div><Label>Fax Number</Label><Input data-testid="ta-fax" value={form.fax_number} onChange={e => updateField('fax_number', e.target.value)} className="mt-1.5" placeholder="(02) XXXX XXXX" /></div>
        <div className="md:col-span-2"><Label>Postal Address</Label><Input data-testid="ta-postal-address" value={form.postal_address} onChange={e => updateField('postal_address', e.target.value)} className="mt-1.5" placeholder="PO Box or street" /></div>
        <div className="md:col-span-2 border-t pt-6 mt-4"><h3 className="font-semibold text-slate-800 mb-4">Principal Contact</h3></div>
        <div><Label>Contact Name *</Label><Input data-testid="ta-principal-contact" value={form.principal_contact} onChange={e => updateField('principal_contact', e.target.value)} className="mt-1.5" placeholder="Full name" /></div>
        <div><Label>Email *</Label><Input data-testid="ta-email" type="email" value={form.email} onChange={e => updateField('email', e.target.value)} className="mt-1.5" placeholder="email@business.com" /></div>
        <div><Label>Mobile *</Label><Input data-testid="ta-mobile" value={form.mobile} onChange={e => updateField('mobile', e.target.value)} className="mt-1.5" placeholder="04XX XXX XXX" /></div>
      </div>
    </div>
  );
}

function Step2({ directors, updateDirector, addDirector, removeDirector }) {
  const handleLicenceUpload = async (index, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('File must be less than 10MB'); return; }
    try {
      const dataUrl = await fileToCompressedDataUrl(file);
      updateDirector(index, 'licence_file', dataUrl);
    } catch (err) {
      toast.error(err?.message || 'Could not read file. Try a different one.');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Users className="w-6 h-6 text-[#01195d]" />
          <h2 className="text-xl font-bold text-[#0f172a]">Directors / Proprietors</h2>
        </div>
        {directors.length < 4 && <Button variant="outline" size="sm" onClick={addDirector}><Plus className="w-4 h-4 mr-1" />Add</Button>}
      </div>
      <p className="text-slate-600 text-sm mb-6">Provide details for all directors or proprietors associated with this business.</p>
      {directors.map((d, i) => (
        <div key={i} className="border rounded-xl p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-slate-800">Person {i + 1}</h3>
            {directors.length > 1 && <Button variant="ghost" size="sm" onClick={() => removeDirector(i)} className="text-red-500"><X className="w-4 h-4" /></Button>}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2"><Label>Full Name *</Label><Input data-testid={`ta-director-name-${i}`} value={d.name} onChange={e => updateDirector(i, 'name', e.target.value)} className="mt-1.5" placeholder="Full legal name" /></div>
            <div className="md:col-span-2"><Label>Address *</Label><Input data-testid={`ta-director-address-${i}`} value={d.address} onChange={e => updateDirector(i, 'address', e.target.value)} className="mt-1.5" placeholder="Home address" /></div>
            <div><Label>Date of Birth</Label><Input data-testid={`ta-director-dob-${i}`} type="date" value={d.dob} onChange={e => updateDirector(i, 'dob', e.target.value)} className="mt-1.5" /></div>
            <div className="md:col-span-2">
              <Label>Driver's Licence (Required) *</Label>
              <div className="mt-1.5">
                {d.licence_file ? (
                  <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span data-testid={`ta-licence-uploaded-${i}`} className="text-sm text-green-700 flex-1">Licence uploaded</span>
                    <Button variant="ghost" size="sm" onClick={() => updateDirector(i, 'licence_file', '')} className="text-red-500 hover:text-red-700"><X className="w-4 h-4" /></Button>
                  </div>
                ) : (
                  <label className="flex items-center justify-center w-full h-24 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-[#01195d] hover:bg-slate-50 transition-colors">
                    <div className="text-center">
                      <Upload className="w-6 h-6 text-slate-400 mx-auto mb-1" />
                      <span className="text-sm text-slate-500">Upload Driver's Licence</span>
                      <p className="text-xs text-slate-400 mt-0.5">JPG/PNG (compressed) or PDF (max 2MB)</p>
                    </div>
                    <input data-testid={`ta-licence-input-${i}`} type="file" accept="image/*,.pdf" className="hidden" onChange={e => handleLicenceUpload(i, e)} />
                  </label>
                )}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function Step3({ refs, updateRef, addRef, removeRef }) {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Briefcase className="w-6 h-6 text-[#01195d]" />
          <h2 className="text-xl font-bold text-[#0f172a]">Trade References</h2>
        </div>
        {refs.length < 3 && <Button variant="outline" size="sm" onClick={addRef}><Plus className="w-4 h-4 mr-1" />Add</Button>}
      </div>
      <p className="text-slate-600 text-sm mb-6">Provide details of other suppliers you have credit accounts with.</p>
      {refs.map((r, i) => (
        <div key={i} className="border rounded-xl p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-slate-800">Reference {i + 1}</h3>
            {refs.length > 1 && <Button variant="ghost" size="sm" onClick={() => removeRef(i)} className="text-red-500"><X className="w-4 h-4" /></Button>}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><Label>Company *</Label><Input data-testid={`ta-ref-company-${i}`} value={r.company} onChange={e => updateRef(i, 'company', e.target.value)} className="mt-1.5" placeholder="Supplier name" /></div>
            <div><Label>Contact *</Label><Input data-testid={`ta-ref-contact-${i}`} value={r.contact} onChange={e => updateRef(i, 'contact', e.target.value)} className="mt-1.5" placeholder="Contact person" /></div>
            <div><Label>Phone *</Label><Input data-testid={`ta-ref-phone-${i}`} value={r.phone} onChange={e => updateRef(i, 'phone', e.target.value)} className="mt-1.5" placeholder="(02) XXXX XXXX" /></div>
            <div><Label>Email</Label><Input data-testid={`ta-ref-email-${i}`} type="email" value={r.email} onChange={e => updateRef(i, 'email', e.target.value)} className="mt-1.5" placeholder="contact@supplier.com" /></div>
          </div>
        </div>
      ))}
    </div>
  );
}

function Step4({ form, updateField }) {
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <CreditCard className="w-6 h-6 text-[#01195d]" />
        <h2 className="text-xl font-bold text-[#0f172a]">Credit Required</h2>
      </div>
      <div className="max-w-md">
        <Label>Monthly Credit Required *</Label>
        <div className="relative mt-1.5">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
          <Input data-testid="ta-monthly-credit" value={form.monthly_credit} onChange={e => updateField('monthly_credit', e.target.value)} className="pl-8" placeholder="0.00" />
        </div>
      </div>
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mt-8">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
          <div>
            <h4 className="font-semibold text-amber-800 mb-2">Payment Terms</h4>
            <ul className="text-amber-700 text-sm space-y-1">
              <li>• Invoices due within 30 days</li>
              <li>• No further credit if invoices exceed 30 days</li>
              <li>• Interest may be charged on overdue amounts</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

function Step5({ form, updateField }) {
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <FileText className="w-6 h-6 text-[#01195d]" />
        <h2 className="text-xl font-bold text-[#0f172a]">Declaration & Agreement</h2>
      </div>
      <p className="text-slate-600 text-sm mb-6">Please read and agree to the following declarations.</p>
      <div className="space-y-4">
        <div className="border rounded-xl p-5"><div className="flex items-start gap-3"><Checkbox data-testid="ta-declaration1" id="d1" checked={form.declaration1} onCheckedChange={v => updateField('declaration1', v)} className="mt-1" /><label htmlFor="d1" className="text-sm text-slate-700 cursor-pointer">I/We authorise GS Pacific Marketing Aust Pty Ltd to seek consumer credit information from a credit reporting body for the purpose of assessing this commercial credit application.</label></div></div>
        <div className="border rounded-xl p-5"><div className="flex items-start gap-3"><Checkbox data-testid="ta-declaration2" id="d2" checked={form.declaration2} onCheckedChange={v => updateField('declaration2', v)} className="mt-1" /><label htmlFor="d2" className="text-sm text-slate-700 cursor-pointer">I/We agree that GS Pacific Marketing Aust Pty Ltd may exchange personal information with other credit providers for assessment.</label></div></div>
        <div className="border rounded-xl p-5"><div className="flex items-start gap-3"><Checkbox data-testid="ta-declaration3" id="d3" checked={form.declaration3} onCheckedChange={v => updateField('declaration3', v)} className="mt-1" /><label htmlFor="d3" className="text-sm text-slate-700 cursor-pointer">I/We authorise GS Pacific Marketing Aust Pty Ltd to obtain a consumer credit report for collecting overdue payments.</label></div></div>
        <div className="border rounded-xl p-5"><div className="flex items-start gap-3"><Checkbox data-testid="ta-declaration4" id="d4" checked={form.declaration4} onCheckedChange={v => updateField('declaration4', v)} className="mt-1" /><label htmlFor="d4" className="text-sm text-slate-700 cursor-pointer">I/We undertake to be liable for and reimburse any legal or other expenses incurred in collection of any debt owed.</label></div></div>
      </div>
      <div className="border-t pt-6 mt-8">
        <h3 className="font-semibold text-slate-800 mb-4">Authorised Signatory</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><Label>Full Name *</Label><Input data-testid="ta-signatory-name" value={form.signatory_name} onChange={e => updateField('signatory_name', e.target.value)} className="mt-1.5" placeholder="Your full name" /></div>
          <div><Label>Date</Label><Input data-testid="ta-signature-date" type="date" value={form.signature_date} onChange={e => updateField('signature_date', e.target.value)} className="mt-1.5" /></div>
        </div>
        <p className="text-xs text-slate-500 mt-4">By submitting, you confirm all information is accurate.</p>
      </div>
    </div>
  );
}

export const TradeApplicationPage = () => {
  const [form, setForm] = useState({
    company_name: '', trading_name: '', abn: '', years_trading: '', trading_address: '', trading_postcode: '',
    postal_address: '', postal_postcode: '', business_phone: '', fax_number: '', principal_contact: '', email: '', mobile: '',
    directors: [{ name: '', address: '', dob: '', licence_file: '' }],
    trade_references: [{ company: '', contact: '', phone: '', email: '' }],
    monthly_credit: '', declaration1: false, declaration2: false, declaration3: false, declaration4: false,
    signatory_name: '', signature_date: ''
  });
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const updateField = (field, value) => setForm(p => ({ ...p, [field]: value }));
  const updateDirector = (i, field, value) => setForm(p => { const d = [...p.directors]; d[i] = { ...d[i], [field]: value }; return { ...p, directors: d }; });
  const addDirector = () => form.directors.length < 4 && setForm(p => ({ ...p, directors: [...p.directors, { name: '', address: '', dob: '', licence_file: '' }] }));
  const removeDirector = (i) => form.directors.length > 1 && setForm(p => ({ ...p, directors: p.directors.filter((_, idx) => idx !== i) }));
  const updateRef = (i, field, value) => setForm(p => { const r = [...p.trade_references]; r[i] = { ...r[i], [field]: value }; return { ...p, trade_references: r }; });
  const addRef = () => form.trade_references.length < 3 && setForm(p => ({ ...p, trade_references: [...p.trade_references, { company: '', contact: '', phone: '', email: '' }] }));
  const removeRef = (i) => form.trade_references.length > 1 && setForm(p => ({ ...p, trade_references: p.trade_references.filter((_, idx) => idx !== i) }));

  const validate = (s) => {
    if (s === 1) return form.company_name && form.abn && form.trading_address && form.email && form.mobile && form.principal_contact;
    if (s === 2) return form.directors.every(d => d.name && d.address && d.licence_file);
    if (s === 3) return form.trade_references.every(r => r.company && r.contact && r.phone);
    if (s === 4) return form.monthly_credit;
    if (s === 5) return form.declaration1 && form.declaration2 && form.declaration3 && form.declaration4 && form.signatory_name;
    return true;
  };

  const scrollTop = () => {
    // Use rAF so the scroll happens after the new step has mounted, avoiding
    // Chrome scroll-anchoring cancellation when the page height shrinks.
    requestAnimationFrame(() => window.scrollTo(0, 0));
  };

  useEffect(() => { scrollTop(); }, [step, submitted]);

  const next = () => {
    if (!validate(step)) { toast.error('Please fill required fields'); return; }
    setStep(s => Math.min(s + 1, 5));
  };
  const prev = () => { setStep(s => Math.max(s - 1, 1)); };

  const submit = async () => {
    if (!validate(5)) { toast.error('Complete all declarations'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/trade-application`, form);
      setSubmitted(true);
      toast.success('Application submitted!');
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map(d => d.msg || JSON.stringify(d)).join('; ')
        : typeof detail === 'string'
          ? detail
          : e?.response?.status
            ? `Submission failed (${e.response.status})`
            : e?.message || 'Submission failed. Please try again.';
      toast.error(msg);
    }
    finally { setSubmitting(false); }
  };

  if (submitted) return <SuccessScreen />;

  return (
    <div className="min-h-screen bg-slate-50 pt-20" data-testid="trade-application-page">
      <Toaster position="top-right" richColors />
      <div className="bg-[#01195d] py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">Trade Credit Application</h1>
          <p className="text-slate-300 max-w-2xl mx-auto">Complete this application to apply for a trade credit account with GS Pacific.</p>
        </div>
      </div>
      <div className="max-w-4xl mx-auto px-4 py-8">
        <StepProgress currentStep={step} />
      </div>
      <div className="max-w-4xl mx-auto px-4 pb-20">
        <div className="bg-white rounded-2xl shadow-sm p-6 md:p-10">
          {step === 1 && <Step1 form={form} updateField={updateField} />}
          {step === 2 && <Step2 directors={form.directors} updateDirector={updateDirector} addDirector={addDirector} removeDirector={removeDirector} />}
          {step === 3 && <Step3 refs={form.trade_references} updateRef={updateRef} addRef={addRef} removeRef={removeRef} />}
          {step === 4 && <Step4 form={form} updateField={updateField} />}
          {step === 5 && <Step5 form={form} updateField={updateField} />}
          <div className="flex justify-between mt-10 pt-6 border-t">
            <Button data-testid="ta-prev-button" variant="outline" onClick={prev} disabled={step === 1} className="h-12 px-6">Previous</Button>
            {step < 5 ? (
              <Button data-testid="ta-next-button" onClick={next} className="bg-[#01195d] hover:bg-[#01195d]/90 h-12 px-8">Continue</Button>
            ) : (
              <Button data-testid="ta-submit-button" onClick={submit} disabled={submitting} className="bg-[#01195d] hover:bg-[#01195d]/90 h-12 px-8">{submitting ? 'Submitting...' : 'Submit Application'}</Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TradeApplicationPage;
